/**
 * Turn Code Review Service
 *
 * Provides the logic for the turn-code review gate:
 * - Analyse a book's words against the turn_color_words lexicon
 * - Save/update individual turn codes
 * - Approve a book (transition turn_codes_pending → ready + notify parents)
 *
 * Pure data functions — no React. Supabase is the only external dependency
 * so tests can mock at the query boundary.
 */

import { supabase } from '@/lib/supabase/client';
import { parseTurnCode } from '@/lib/turn-color';
import { approveBookTurnCodes as doApprove } from './content-editor';

// ============================================================================
// Types
// ============================================================================

export interface WordTurnCodeStatus {
  word: string;
  cleanWord: string;
  turnCode: string | null;
  skillId: string | null;
  inLexicon: boolean;
  pageNumber: number;
}

export interface BookTurnCodeReport {
  bookId: string;
  totalWords: number;
  uniqueWords: number;
  matchedWords: number;
  missingWords: number;
  coveragePct: number;
  words: WordTurnCodeStatus[];
}

export interface PendingBook {
  id: string;
  title: string;
  author: string | null;
  isbn: string | null;
  coverImageUrl: string | null;
  createdAt: string | null;
  qualityStatus?: string;
  contentSource?: string | null;
  generationMetadata?: Record<string, unknown> | null;
}

// ============================================================================
// Pure helpers (exported for testing)
// ============================================================================

/** Lowercase + strip everything except a-z. */
export function cleanWord(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z]/g, '');
}

/** Split a sentence/page text into raw word tokens. */
export function extractWords(text: string): string[] {
  return text.split(/\s+/).filter(w => w.length > 0);
}

// ============================================================================
// Turn code suggestion engine
// ============================================================================

const VOWELS = new Set('aeiou');

/** 'y' is a vowel everywhere except word-initial position (e.g. "yes" = consonant, "happy"/"gym" = vowel). */
function isVowelAt(char: string, index: number): boolean {
  if (VOWELS.has(char)) return true;
  return char === 'y' && index > 0;
}

/**
 * Known vowel digraphs/trigraphs in English, ordered longest-first so
 * greedy matching picks "igh" before "ig".
 */
const VOWEL_DIGRAPHS = [
  'igh', 'eigh',
  'ai', 'ay', 'ea', 'ee', 'ei', 'ey',
  'ie', 'oa', 'oe', 'oi', 'oo', 'ou', 'ow', 'oy',
  'ue', 'ui',
];

/** Consonant digraphs that should stay together as plain text. */
const CONSONANT_DIGRAPHS = [
  'th', 'sh', 'ch', 'wh', 'ph', 'ck', 'ng', 'gh', 'kn', 'wr', 'gn', 'mb',
  'tch', 'dge',
];

/**
 * Generate a suggested turn code for a word.
 *
 * Splits the word into consonant (plain) segments and vowel g-p values.
 * Each vowel g-p value gets a placeholder phoneme "___" that the admin
 * replaces with the actual color from the curriculum. This gives structural
 * scaffolding so the admin only needs to assign phoneme colors, not write
 * the vowel g-p value syntax from scratch.
 *
 * Example: "cat" → "c, [a-___], t"
 *          "tree" → "t, r, [ee-___]"
 *          "she" → "sh, [e-___]"
 *          "happy" → "h, [a-___], p, p, [y-___]"
 *          "yes" → "y, [e-___], s"
 */
export function suggestTurnCode(word: string): string {
  const w = word.toLowerCase();
  const segments: string[] = [];
  let i = 0;

  while (i < w.length) {
    // Try vowel digraphs (longest first)
    let matched = false;
    for (const vd of VOWEL_DIGRAPHS) {
      if (w.startsWith(vd, i)) {
        segments.push(`[${vd}-___]`);
        i += vd.length;
        matched = true;
        break;
      }
    }
    if (matched) continue;

    // Try consonant digraphs (longest first)
    for (const cd of CONSONANT_DIGRAPHS) {
      if (w.startsWith(cd, i)) {
        segments.push(cd);
        i += cd.length;
        matched = true;
        break;
      }
    }
    if (matched) continue;

    // Single vowel (including non-initial 'y')
    if (isVowelAt(w[i], i)) {
      segments.push(`[${w[i]}-___]`);
      i++;
      continue;
    }

    // Single consonant
    segments.push(w[i]);
    i++;
  }

  return segments.join(', ');
}

// ============================================================================
// Skill ID suggestion
// ============================================================================

export interface SkillOption {
  skill_id: string;
  color: string;
  phase: string;
  display_name: string;
  sequence_order: number;
}

/**
 * Infer the most likely skill_id for a turn code by extracting the dominant
 * vowel color. Returns null when ambiguous (multiple colors) or when no
 * vowel segments exist.
 */
export function suggestSkillId(
  turnCode: string,
  skills: SkillOption[]
): string | null {
  if (!turnCode || skills.length === 0) return null;

  const segments = parseTurnCode(turnCode);
  const colors = new Set(
    segments
      .filter(s => s.type === 'color' && s.color)
      .map(s => s.color!)
  );

  if (colors.size !== 1) return null;
  const color = [...colors][0];
  const match = skills.find(s => s.color === color);
  return match?.skill_id ?? null;
}

// ============================================================================
// Data access
// ============================================================================

/**
 * Fetch all books currently waiting for turn-code review.
 */
export async function getPendingBooks(): Promise<PendingBook[]> {
  const { data, error } = await supabase
    .from('scanned_books')
    .select('id, title, author, isbn, cover_image_url, created_at')
    .eq('quality_status', 'turn_codes_pending')
    .is('deleted_at', null)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[TurnCodeReview] Error fetching pending books:', error);
    return [];
  }

  return (data || []).map(b => ({
    id: b.id,
    title: b.title,
    author: b.author,
    isbn: b.isbn,
    coverImageUrl: b.cover_image_url,
    createdAt: b.created_at,
    qualityStatus: 'turn_codes_pending',
  }));
}

/**
 * Fetch a single book by ID regardless of quality_status.
 * Used for deep-linking from the catalog into turn-code review.
 */
export async function getBookForReview(bookId: string): Promise<PendingBook | null> {
  const { data, error } = await supabase
    .from('scanned_books')
    .select('id, title, author, isbn, cover_image_url, created_at, quality_status')
    .eq('id', bookId)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    title: data.title,
    author: data.author,
    isbn: data.isbn,
    coverImageUrl: data.cover_image_url,
    createdAt: data.created_at,
    qualityStatus: data.quality_status ?? undefined,
  };
}

/**
 * Build a full turn-code report for a book by cross-referencing its
 * digital_pages text against the turn_color_words lexicon.
 */
export async function getBookTurnCodeStatus(bookId: string): Promise<BookTurnCodeReport> {
  const { data: pages, error: pagesErr } = await supabase
    .from('digital_pages')
    .select('digital_page_number, sentence_text')
    .eq('book_id', bookId)
    .order('digital_page_number', { ascending: true });

  if (pagesErr) {
    console.error('[TurnCodeReview] Error fetching digital pages:', pagesErr);
    return { bookId, totalWords: 0, uniqueWords: 0, matchedWords: 0, missingWords: 0, coveragePct: 100, words: [] };
  }

  const wordEntries: { word: string; cleanWord: string; pageNumber: number }[] = [];
  for (const page of pages || []) {
    if (!page.sentence_text) continue;
    const rawWords = extractWords(page.sentence_text);
    for (const w of rawWords) {
      const cw = cleanWord(w);
      if (cw.length > 0) {
        wordEntries.push({ word: w, cleanWord: cw, pageNumber: page.digital_page_number });
      }
    }
  }

  const uniqueClean = [...new Set(wordEntries.map(e => e.cleanWord))];

  const codeMap = new Map<string, { turnCode: string; skillId: string | null }>();
  if (uniqueClean.length > 0) {
    const { data: tcData, error: tcErr } = await supabase
      .from('turn_color_words')
      .select('word, turn_code, skill_id')
      .in('word', uniqueClean);

    if (!tcErr && tcData) {
      for (const row of tcData) {
        codeMap.set(row.word.toLowerCase(), { turnCode: row.turn_code, skillId: row.skill_id || null });
      }
    }
  }

  const words: WordTurnCodeStatus[] = wordEntries.map(e => {
    const lookup = codeMap.get(e.cleanWord);
    return {
      word: e.word,
      cleanWord: e.cleanWord,
      turnCode: lookup?.turnCode ?? null,
      skillId: lookup?.skillId ?? null,
      inLexicon: !!lookup,
      pageNumber: e.pageNumber,
    };
  });

  const uniqueInLexicon = new Set(words.filter(w => w.inLexicon).map(w => w.cleanWord)).size;
  const uniqueMissing = new Set(words.filter(w => !w.inLexicon).map(w => w.cleanWord)).size;
  const totalUnique = uniqueClean.length;
  const coveragePct = totalUnique > 0 ? Math.round((uniqueInLexicon / totalUnique) * 100) : 100;

  return {
    bookId,
    totalWords: wordEntries.length,
    uniqueWords: totalUnique,
    matchedWords: uniqueInLexicon,
    missingWords: uniqueMissing,
    coveragePct,
    words,
  };
}

/**
 * Save or update a single turn code via the turn-color-sync edge function.
 * Requires admin auth (enforced by the edge function).
 */
export async function saveTurnCode(
  word: string,
  turnCode: string,
  skillId?: string
): Promise<{ success: boolean; error?: string }> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) {
    return { success: false, error: 'Not authenticated' };
  }

  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/turn-color-sync`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.session.access_token}`,
        apikey: anonKey,
      },
      body: JSON.stringify({ action: 'save_word', word, turn_code: turnCode, ...(skillId && { skill_id: skillId }) }),
    }
  );

  const json = await res.json();
  if (!res.ok || !json.success) {
    return { success: false, error: json.error || 'Failed to save turn code' };
  }

  return { success: true };
}

/**
 * Approve a book's turn codes — delegates to content-editor's approval logic.
 */
export async function approveBook(bookId: string): Promise<{ success: boolean; parentsNotified: number }> {
  return doApprove(bookId);
}
